import * as styles from "./TabLayout.css";

import type { JSX } from "react";

import { Input } from "@frontend/components/atoms/Input";
import { Separator } from "@frontend/components/atoms/Separator";
import {
  ContactEmailPlaceLabels,
  ContactPhonePlaceLabels,
  ContactUrlPlaceLabels,
} from "@frontend/features/addressbook";
import { useField } from "@frontend/hooks/form";
import type { TextField } from "@frontend/lib/form";
import { useLingui } from "@lingui/react/macro";

import { clientForm } from "../stores/editor";
import { AddressList } from "./AddressList";
import { ChannelList } from "./ChannelList";
import { SectionFrame } from "./Section";
import { TabColumn } from "./TabColumn";

const fields = clientForm.fields;

/**
 * How the client is reached, on a tab of its own: the channels on the left,
 * the addresses on the right. A client holds one address per site, so the list
 * is as long as the sites it is served at, which is more than a block beside
 * the identity can take.
 */
export function ContactDetailsTab(): JSX.Element {
  const { t } = useLingui();

  return (
    <div className={styles.tab}>
      <div className={styles.columns}>
        <TabColumn>
          <SectionFrame icon="Phone" legend={t`Phones`}>
            <ChannelList
              field={fields.phones}
              blank={{ place: "CELL", number: "" }}
              labels={ContactPhonePlaceLabels}
              place={(item) => item.fields.place}
              addLabel={t`Add a phone`}
              emptyLabel={t`No phone number.`}
            >
              {(item) => <ChannelValue field={item.fields.number} />}
            </ChannelList>
          </SectionFrame>

          <SectionFrame icon="Mail" legend={t`Emails`}>
            <ChannelList
              field={fields.emails}
              blank={{ place: "WORK", address: "" }}
              labels={ContactEmailPlaceLabels}
              place={(item) => item.fields.place}
              addLabel={t`Add an email`}
              emptyLabel={t`No email address.`}
            >
              {(item) => <ChannelValue field={item.fields.address} />}
            </ChannelList>
          </SectionFrame>

          <SectionFrame icon="Globe" legend={t`Web sites`}>
            <ChannelList
              field={fields.urls}
              blank={{ place: "WORK", url: "" }}
              labels={ContactUrlPlaceLabels}
              place={(item) => item.fields.place}
              addLabel={t`Add a web site`}
              emptyLabel={t`No web site.`}
            >
              {(item) => <ChannelValue field={item.fields.url} />}
            </ChannelList>
          </SectionFrame>
        </TabColumn>

        <Separator orientation="vertical" className={styles.separator} />

        <TabColumn>
          <SectionFrame icon="MapPin" legend={t`Addresses`}>
            <AddressList />
          </SectionFrame>
        </TabColumn>
      </div>
    </div>
  );
}

type ChannelValueProps = {
  field: TextField;
};

/** The value beside its place, whatever the line holds. */
function ChannelValue({ field }: ChannelValueProps): JSX.Element {
  const { value, setValue, error } = useField(field);

  return (
    <Input
      value={value ?? ""}
      size="sm"
      invalid={Boolean(error)}
      onValueChange={(next) => setValue(next)}
    />
  );
}
